import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { CartStatus } from '@/features/cart/types';
import CartHeroSection from '@/features/cart/components/CartHeroSection';
import CartItemCard from '@/features/cart/components/CartItemCard';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useCartData } from '@/features/cart/hooks/useCartData';
import { useCartItemStatuses } from '@/features/cart/hooks/useCartItemStatuses';
import { usePaymentTimer } from '@/features/cart/hooks/usePaymentTimer';
import { usePriceConfirmation } from '@/features/cart/hooks/usePriceConfirmation';
import { useCheckout } from '@/features/cart/hooks/useCheckout';
import { usePaymentStatusFeedback } from '@/features/cart/hooks/usePaymentStatusFeedback';
import { useCartConflicts } from '@/features/cart/hooks/useCartConflicts';
import AvailabilityConflictBanner from '@/features/cart/components/AvailabilityConflictBanner';
import AddOnConflictBanner from '@/features/cart/components/AddOnConflictBanner';
import FloatingCheckoutButton from '@/features/cart/components/FloatingCheckoutButton';
import FloatingPriceSummary from '@/features/booking/components/FloatingPriceSummary';
import ConfirmAllPricesBanner from '@/features/cart/components/ConfirmAllPricesBanner';
import CartLoadingState from '@/features/cart/components/CartLoadingState';
import CartErrorState from '@/features/cart/components/CartErrorState';
import CartStatusBanner from '@/features/cart/components/CartStatusBanner';
import { GuestCheckoutModal } from '@/features/cart/components/GuestCheckoutModal';
import { GuestCheckoutConfirmationModal } from '@/features/cart/components/GuestCheckoutConfirmationModal';
import { formatTimeReadable } from '@/lib/utils';

import { CartItemStatusTestProvider, GlobalCartItemStatusSwitcher } from '@/features/cart/components/CartItemStatusSwitcher';

const CartPage: React.FC = () => {
  const { cartId } = useParams<{ cartId: string }>();
  
  // Handle payment status feedback from query params
  usePaymentStatusFeedback();

  // Fetch cart data
  const {
    cart,
    addOns,
    eventBookingCategories,
    cartResponseWithSecret,
    loading,
    error,
    refetch: refetchCart,
  } = useCartData(cartId);

  // Fetch and manage item statuses
  const { itemStatuses, setItemStatuses } = useCartItemStatuses(cart);

  // Fetch and manage conflicts
  const {
    hasConflict,
    availabilityConflicts,
    addOnConflicts,
    refetch: refetchConflicts,
  } = useCartConflicts(cart, itemStatuses);

  // Ref to hold the expired cancel handler (solves circular dependency)
  const expiredCancelRef = React.useRef<(() => void) | null>(null);

  // Payment timer with expiration callback
  // When timer expires while on cart page, auto-cancel checkout and reload
  const handleTimerExpire = useCallback(() => {
    expiredCancelRef.current?.();
  }, []);

  const { timeRemaining, isExpired, formattedTime } = usePaymentTimer(cart, {
    onExpire: handleTimerExpire,
  });

  // Price confirmation
  const {
    itemsNeedingPriceConfirmation,
    confirmingAll,
    confirmAllPrices,
  } = usePriceConfirmation(cart, itemStatuses, setItemStatuses);

  // Checkout operations
  const {
    canProceed,
    isStatusesLoading,
    isCheckoutProcessing,
    isCancelingCheckout,
    isDownloadingInvoice,
    showGuestCheckoutForm,
    showGuestConfirmationModal,
    guestCheckoutData,
    checkoutCooldownUntil,
    isCooldownActive,
    handleCheckout,
    handleCancelCheckout,
    handleExpiredCancelCheckout,
    handleDownloadInvoice,
    handleGuestCheckoutSubmit,
    closeGuestCheckoutForm,
    closeGuestConfirmationModal,
    handleGuestConfirmationSuccess,
  } = useCheckout(cartId, cart, cartResponseWithSecret, timeRemaining, refetchCart, itemStatuses, hasConflict);

  // Update the ref with the latest handler
  React.useEffect(() => {
    expiredCancelRef.current = handleExpiredCancelCheckout;
  }, [handleExpiredCancelCheckout]);

  // Handlers
  const handleCartUpdate = async () => {
    await refetchCart();
    // Refetch conflicts after cart update
    if (cart?.status === CartStatus.Active) {
      await refetchConflicts();
    }
  };

  const handleCartItemRemove = async () => {
    await refetchCart();
    // Refetch conflicts after item removal
    if (cart?.status === CartStatus.Active) {
      await refetchConflicts();
    }
  };

  // Check if cart is completed
  const isCartCompleted = cart && (cart.status === CartStatus.Completed || cart.status === CartStatus.SuccessfulPayment);



  // Timer display conditions - show for both PENDING_PAYMENT and FAILED_PAYMENT
  // Safe access for cart properties since cart might be null during loading
  const showTimer = cart && (
    cart.status === CartStatus.PendingPayment || 
    cart.status === CartStatus.FailedPayment
  ) && cart.payment_intent_expires_at;
  
  const isTimerExpired = Boolean(cart && (
    cart.status === CartStatus.PendingPayment || 
    cart.status === CartStatus.FailedPayment
  ) && isExpired);



  return (
    <>
      <CartItemStatusTestProvider>
        <GlobalCartItemStatusSwitcher />
        <Toaster 
        position="bottom-right" 
        toastOptions={{
          style: { 
            background: '#1f2937', 
            color: '#fff',
            border: '1px solid #374151'
          },
          duration: 4000,
        }} 
      />
      <Header />

      {loading ? (
        <CartLoadingState />
      ) : (error || !cart) ? (
        <CartErrorState error={error} />
      ) : (
        <div className="min-h-screen bg-black">
          {/* Hero Section */}
          <CartHeroSection business={cart.business} />

          {/* Cart Items Section */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            {/* Cart Summary Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-3xl font-bold text-white">Cart Items</h2>
                {/* Guest Cart Badge */}
                {cart.is_guest_cart && (
                  <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-gray-700/80 backdrop-blur-sm border border-gray-500/60 shadow-lg">
                    <svg 
                      className="w-5 h-5 text-gray-300" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" 
                      />
                    </svg>
                    <span className="text-sm font-semibold text-gray-300">Guest Cart</span>
                  </div>
                )}
              </div>
              <p className="text-gray-400">
                {cart.items?.length || 0} {cart.items?.length === 1 ? 'item' : 'items'} in your cart
              </p>
            </div>

            {/* Status Banner */}
            <CartStatusBanner
              status={cart.status}
              onCancelCheckout={handleCancelCheckout}
              isCanceling={isCancelingCheckout}
              isExpired={isExpired}
            />

            {/* Checkout Cooldown Banner */}
            {isCooldownActive && cart.status === CartStatus.Active && checkoutCooldownUntil && (
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-6 h-6 rounded-full bg-orange-500/20 flex items-center justify-center">
                      <svg className="w-4 h-4 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-orange-400 font-semibold text-sm mb-1">Checkout Temporarily Unavailable</h3>
                    <p className="text-gray-300 text-sm">
                      You have attempted maximum number of checkout cancellation in last 2 hours. Please wait till {formatTimeReadable(checkoutCooldownUntil)} for checking out.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Add-On Conflict Banner */}
            {addOnConflicts.length > 0 && (
              <AddOnConflictBanner
                addOnConflicts={addOnConflicts}
                addOns={addOns}
                clubHandle={cart.business?.handle || null}
              />
            )}

            {/* Cart Items List */}
            {cart.items && cart.items.length > 0 ? (
              <div className="space-y-6 mb-12">
                {cart.items.map((item) => {
                  const conflictingItemIds = availabilityConflicts[item.id] || [];
                  const hasAvailabilityConflict = conflictingItemIds.length > 0;

                  return (
                    <div key={item.id} id={`cart-item-${item.id}`}>
                      {/* Availability Conflict Banner */}
                      {hasAvailabilityConflict && (
                        <AvailabilityConflictBanner
                          cartItemId={item.id}
                          conflictingItemIds={conflictingItemIds}
                          cartItems={cart.items || []}
                          eventBookingCategories={eventBookingCategories}
                          onItemRemoved={handleCartItemRemove}
                        />
                      )}

                      <CartItemCard
                        cartItem={item}
                        addOns={addOns}
                        eventBookingCategories={eventBookingCategories}
                        onUpdate={handleCartUpdate}
                        onRemove={handleCartItemRemove}
                        itemStatus={itemStatuses[item.id]}
                        cartStatus={cart.status}
                        clubHandle={cart.business?.handle || null}
                        isGuestCart={cart.is_guest_cart}
                        onStatusUpdate={(itemId, status) => {
                          setItemStatuses(prev => ({
                            ...prev,
                            [itemId]: status
                          }));
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-gray-400 text-lg">No items in cart</p>
              </div>
            )}

            {/* Confirm All Prices Button */}
            {itemsNeedingPriceConfirmation.length > 1 && (
              <ConfirmAllPricesBanner
                itemsCount={itemsNeedingPriceConfirmation.length}
                onConfirm={confirmAllPrices}
                confirmingAll={confirmingAll}
              />
            )}
          </div>
        </div>
      )}
      <Footer />

      {/* Floating Price Summary (for completed carts) or Floating Checkout Button */}
      {/* Only show if cart is loaded and valid */}
      {!loading && !error && cart && cart.items && cart.items.length > 0 && (
        isCartCompleted ? (
          <FloatingPriceSummary
            isCartMode={true}
            onDownloadInvoice={handleDownloadInvoice}
            isDownloadingInvoice={isDownloadingInvoice}
            cartItems={cart.items}
            cart={cart}
            priceBreakdown={null}
            guestCount={0}
            eventBookingCategory={{
              booking_category: {
                name: '',
                business: null,
              },
              price: 0,
            }}
            onAddToCart={() => {}}
            addingToCart={false}
          />
        ) : (
          <FloatingCheckoutButton
            cartStatus={cart.status}
            canProceed={canProceed}
            isStatusesLoading={isStatusesLoading}
            isTimerExpired={isTimerExpired}
            isCheckoutProcessing={isCheckoutProcessing}
            onCheckout={handleCheckout}
            items={cart.items}
            cart={cart}
            timeRemaining={showTimer && !isExpired ? timeRemaining : null}
            formattedTime={showTimer && !isExpired ? formattedTime : ''}
            timerIsExpired={isExpired}
            onCancelCheckout={showTimer ? handleCancelCheckout : undefined}
            isCancelingCheckout={isCancelingCheckout}
            isCooldownActive={isCooldownActive}
          />
        )
      )}

      {/* Guest Checkout Modal */}
      <GuestCheckoutModal
        isOpen={showGuestCheckoutForm}
        onClose={closeGuestCheckoutForm}
        onSubmit={handleGuestCheckoutSubmit}
        isSubmitting={isCheckoutProcessing}
      />

      {/* Guest Checkout Confirmation Modal */}
      {guestCheckoutData && cartId && (
        <GuestCheckoutConfirmationModal
          isOpen={showGuestConfirmationModal}
          onClose={closeGuestConfirmationModal}
          cartId={cartId}
          guestData={guestCheckoutData}
          onSuccess={handleGuestConfirmationSuccess}
        />
      )}
      </CartItemStatusTestProvider>
    </>
  );
};

export default CartPage;
